import axios, { AxiosInstance } from 'axios'
import type {
  ApplicantAccessVerifyLinkResponse,
  ApplicantApplicationResponse,
  ApplicationDocument,
} from '@/types/application'

const SESSION_STORAGE_KEY = 'jeducare_applicant_session'
const EXPIRES_STORAGE_KEY = 'jeducare_applicant_session_expires'

export interface ApplicantSessionData {
  session_token: string
  expires_at: string
}

// Create dedicated axios instance for public applicant access to avoid Sanctum collisions
const applicantApi: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

// Request interceptor: attach applicant session token from sessionStorage
applicantApi.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(SESSION_STORAGE_KEY)
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export const applicantAccessService = {
  // Session storage management
  saveSession(sessionToken: string, expiresAt: string): void {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionToken)
    sessionStorage.setItem(EXPIRES_STORAGE_KEY, expiresAt)
  },

  getSession(): ApplicantSessionData | null {
    const token = sessionStorage.getItem(SESSION_STORAGE_KEY)
    const expiresAt = sessionStorage.getItem(EXPIRES_STORAGE_KEY)
    if (!token) return null
    return {
      session_token: token,
      expires_at: expiresAt || '',
    }
  },

  clearSession(): void {
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
    sessionStorage.removeItem(EXPIRES_STORAGE_KEY)
  },

  hasActiveSession(): boolean {
    const session = this.getSession()
    if (!session || !session.session_token) return false
    if (session.expires_at) {
      const expires = new Date(session.expires_at).getTime()
      if (Date.now() > expires) {
        this.clearSession()
        return false
      }
    }
    return true
  },

  // API endpoints
  async verifyLink(token: string): Promise<ApplicantAccessVerifyLinkResponse> {
    const response = await applicantApi.post<ApplicantAccessVerifyLinkResponse>('/applicant-access/verify-link', { token })
    return response.data
  },

  async verifyCode(token: string, code: string): Promise<{ success: boolean; session_token: string; expires_at: string; message?: string }> {
    const response = await applicantApi.post('/applicant-access/verify-code', { token, code })
    if (response.data?.session_token) {
      this.saveSession(response.data.session_token, response.data.expires_at)
    }
    return response.data
  },

  async resendCode(token: string): Promise<{ success: boolean; message: string; otp_expires_at?: string }> {
    const response = await applicantApi.post('/applicant-access/resend-code', { token })
    return response.data
  },

  async requestNewLink(email: string): Promise<{ success: boolean; message: string }> {
    const response = await applicantApi.post('/applicant-access/request-link', { email })
    return response.data
  },

  async getApplication(): Promise<ApplicantApplicationResponse> {
    const response = await applicantApi.get<ApplicantApplicationResponse>('/applicant-access/application')
    return response.data
  },

  async updateApplication(formData: FormData): Promise<ApplicantApplicationResponse> {
    const response = await applicantApi.post<ApplicantApplicationResponse>('/applicant-access/application', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  async uploadDocuments(formData: FormData): Promise<{ success: boolean; message: string; data: ApplicationDocument[] }> {
    const response = await applicantApi.post('/applicant-access/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  async deleteDocument(documentId: number): Promise<{ success: boolean; message: string }> {
    const response = await applicantApi.delete(`/applicant-access/documents/${documentId}`)
    return response.data
  },

  async previewDocument(documentId: number): Promise<Blob> {
    const response = await applicantApi.get(`/applicant-access/documents/${documentId}/preview`, {
      responseType: 'blob',
    })
    return response.data
  },

  async logout(): Promise<void> {
    try {
      await applicantApi.post('/applicant-access/logout')
    } finally {
      this.clearSession()
    }
  },
}

export default applicantAccessService
